import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Trash2, Users, Zap } from 'lucide-react';
import { SlideOver } from '../common/SlideOver';
import { listsApi } from '../../lib/api';
import { useUserStore } from '../../store/userStore';
import { useAuthStore } from '../../store/authStore';
import type { List, ListShare, SharePermission } from '../../types/list';

interface ShareListSheetProps {
  open: boolean;
  list: List;
  onClose: () => void;
  /** Fired after any successful share change so the caller can refresh the list payload. */
  onChanged: () => void;
}

const EVERYONE = '__EVERYONE__';

export function ShareListSheet({ open, list, onClose, onChanged }: ShareListSheetProps) {
  const users = useUserStore((s) => s.users);
  const fetchUsers = useUserStore((s) => s.fetchUsers);
  const me = useAuthStore((s) => s.user);

  const [shares, setShares] = useState<ListShare[] | null>(null);
  const [grantee, setGrantee] = useState('');
  const [permission, setPermission] = useState<SharePermission>('EDIT');
  const [saving, setSaving] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    void fetchUsers();
    setShares(null);
    listsApi.shares(list.id)
      .then(({ shares: s }) => setShares(s))
      .catch(() => { toast.error('Could not load shares'); setShares([]); });
  }, [open, list.id, fetchUsers]);

  const teamShare = useMemo(() => shares?.find((s) => s.granteeType === 'TEAM') ?? null, [shares]);
  const candidates = useMemo(
    () => users.filter((u) => u.id !== list.ownerId && u.id !== me?.id && !shares?.some((s) => s.granteeUserId === u.id)),
    [users, list.ownerId, me?.id, shares],
  );

  async function handleAdd() {
    if (!grantee) return;
    setSaving(true);
    try {
      const input = grantee === EVERYONE
        ? { granteeType: 'TEAM' as const, permission }
        : { granteeType: 'USER' as const, granteeUserId: grantee, permission };
      const { share } = await listsApi.createShare(list.id, input);
      setShares((prev) => [...(prev ?? []), share]);
      setGrantee('');
      toast.success('List shared');
      onChanged();
    } catch {
      toast.error('Could not share the list');
    } finally {
      setSaving(false);
    }
  }

  async function handleRevoke(share: ListShare) {
    setRevokingId(share.id);
    try {
      await listsApi.deleteShare(list.id, share.id);
      setShares((prev) => (prev ?? []).filter((s) => s.id !== share.id));
      toast.success('Share removed');
      onChanged();
    } catch {
      toast.error('Could not remove the share');
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <SlideOver isOpen={open} onClose={onClose} title={`Share "${list.name}"`}>
      <div className="space-y-5">
        {list.kind === 'SMART' && (
          <p className="flex items-start gap-2 text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 rounded-md p-2.5">
            <Zap className="h-4 w-4 flex-none mt-0.5" />
            Smart lists run their filters as each viewer, so another rep may see different results than you do.
          </p>
        )}

        {/* Add a share */}
        <div className="space-y-2">
          <label htmlFor="share-grantee" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Share with
          </label>
          <select
            id="share-grantee"
            className="input w-full"
            value={grantee}
            onChange={(e) => setGrantee(e.target.value)}
          >
            <option value="">Choose a rep…</option>
            {!teamShare && <option value={EVERYONE}>All reps</option>}
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName ?? u.email}
              </option>
            ))}
          </select>
          <div role="radiogroup" aria-label="Permission" className="flex gap-2">
            {(['EDIT', 'VIEW'] as const).map((p) => (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={permission === p}
                onClick={() => setPermission(p)}
                className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${
                  permission === p
                    ? 'border-blast-500 bg-lime-50 dark:bg-lime-900/20 text-gray-900 dark:text-gray-100 font-medium'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {p === 'EDIT' ? 'Can edit' : 'View only'}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {permission === 'EDIT' ? 'They can add, remove, and reorder items.' : 'They can open the list but not change it.'}
          </p>
          <button type="button" className="btn-primary w-full" onClick={() => { void handleAdd(); }} disabled={!grantee || saving}>
            {saving ? 'Sharing…' : 'Share'}
          </button>
        </div>

        {/* Existing shares */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Shared with</h3>
          {shares === null ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
          ) : shares.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No one yet — this list is private.</p>
          ) : (
            <ul className="space-y-2">
              {shares.map((s) => (
                <li key={s.id} className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2">
                  <Users className="h-4 w-4 flex-none text-gray-400" />
                  <span className="flex-1 min-w-0 truncate text-sm text-gray-900 dark:text-gray-100">
                    {s.granteeType === 'TEAM' ? 'All reps' : s.grantee?.displayName ?? s.grantee?.email ?? 'Unknown'}
                  </span>
                  <span className="flex-none text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {s.permission === 'EDIT' ? 'CAN EDIT' : 'VIEW'}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove share for ${s.granteeType === 'TEAM' ? 'all reps' : s.grantee?.displayName ?? 'user'}`}
                    onClick={() => { void handleRevoke(s); }}
                    disabled={revokingId === s.id}
                    className="flex-none p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SlideOver>
  );
}
